import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Cake, RefreshCw, Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGuild } from '@/contexts/GuildContext';
import {
  API, authHeader, inputCls, labelCls, cardCls, btnPrimary,
  Toggle, MONTHS, PageShell, PageLoading, EmptyState, Avatar, useChannelsAndRoles,
} from '@/lib/pageKit';

export default function Birthdays() {
  const { selectedGuild, selectedGuildId } = useGuild();
  const isGlobal = !!selectedGuild?.is_global;
  const [settings, setSettings] = useState({ enabled: false, channel_id: '', role_id: '', message: '' });
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { channels, roles } = useChannelsAndRoles(isGlobal ? null : selectedGuildId);

  const load = useCallback(async () => {
    if (!selectedGuildId || isGlobal) { setLoading(false); return; }
    const [s, b] = await Promise.allSettled([
      axios.get(`${API}/api/birthdays/${selectedGuildId}/settings`, authHeader()),
      axios.get(`${API}/api/birthdays/${selectedGuildId}`, authHeader()),
    ]);
    if (s.status === 'fulfilled') {
      const d = s.value.data || {};
      setSettings({ enabled: !!d.enabled, channel_id: d.channel_id || '', role_id: d.role_id || '', message: d.message || '' });
    } else {
      toast.error(s.reason?.response?.data?.detail || 'Failed to load birthday settings');
    }
    if (b.status === 'fulfilled') setList(b.value.data.birthdays || []);
    setLoading(false);
  }, [selectedGuildId, isGlobal]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const set = (k, v) => setSettings(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/birthdays/${selectedGuildId}/settings`, {
        ...settings, channel_id: settings.channel_id || null, role_id: settings.role_id || null,
      }, authHeader());
      toast.success('Birthday settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageLoading />;

  return (
    <PageShell
      icon={Cake} title="Birthdays" subtitle="Celebrate member birthdays automatically"
      testId="birthdays-main" isGlobal={isGlobal}
      actions={
        <button onClick={load} className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors text-sm font-[Manrope] px-3 py-2 rounded-lg hover:bg-[var(--bg-elevated)] flex-shrink-0">
          <RefreshCw size={14} /> Refresh
        </button>
      }
    >
      <div className={`${cardCls} p-5 space-y-4`}>
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope]">Enable birthday announcements</p>
            <p className="text-[var(--text-muted)] text-xs font-[Manrope]">Members set their birthday from the Member Hub or in Discord</p>
          </div>
          <Toggle checked={settings.enabled} onChange={v => set('enabled', v)} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Announcement channel</label>
            <select value={settings.channel_id} onChange={e => set('channel_id', e.target.value)} className={inputCls}>
              <option value="">— Select a channel —</option>
              {channels.map(c => <option key={c.id} value={c.id}>#{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Birthday role (optional)</label>
            <select value={settings.role_id} onChange={e => set('role_id', e.target.value)} className={inputCls}>
              <option value="">None</option>
              {roles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className={labelCls}>Message</label>
          <textarea rows={3} value={settings.message} onChange={e => set('message', e.target.value)} className={inputCls} placeholder="Happy birthday {user}! 🎉" />
          <p className="text-[var(--text-muted)] text-[11px] font-[Manrope] mt-1">
            Placeholders: <code className="text-[#5865F2]">{'{user}'}</code> mentions the member, <code className="text-[#5865F2]">{'{server}'}</code> is the server name.
          </p>
        </div>
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className={btnPrimary}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
          </button>
        </div>
      </div>

      <div>
        <h2 className="text-[var(--text-primary)] font-semibold font-[Outfit] text-lg mb-3">Upcoming birthdays</h2>
        {list.length === 0 ? (
          <EmptyState icon={Cake} title="No birthdays set yet" hint="Members can add theirs from the Member Hub" />
        ) : (
          <div className={`${cardCls} divide-y divide-[var(--border)] overflow-hidden`}>
            {list.map(b => (
              <div key={b.user_id} className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--bg-hover)] transition-colors">
                <Avatar userId={b.user_id} avatar={b.avatar} name={b.username} size={32} />
                <div className="min-w-0 flex-1">
                  <p className="text-[var(--text-primary)] text-sm font-medium font-[Manrope] truncate">{b.username || b.user_id}</p>
                  <p className="text-[var(--text-muted)] text-xs font-[Manrope]">{MONTHS[(b.month || 1) - 1]} {b.day}</p>
                </div>
                {b.days_until === 0 ? (
                  <span className="text-[11px] font-medium font-[Manrope] px-2 py-0.5 rounded-full bg-pink-500/10 border border-pink-500/20 text-pink-400">Today 🎂</span>
                ) : (
                  <span className="text-xs text-[var(--text-muted)] font-[Manrope]">in {b.days_until} day{b.days_until !== 1 ? 's' : ''}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </PageShell>
  );
}
